import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Tooltip,
  TableSortLabel
} from "@mui/material";
import "../css/StockistTable.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useSalesContext } from "../../../Pages/SalesContext";
import { styled } from "@mui/material/styles";


const StyledButton = styled(Button)({
  margin: "8px 4px 2px 4px",
  padding: "0 1px",
  height: "16px",
  lineHeight: "16px",
  width: "calc(50% - 6px)",
  textAlign: "center",
  fontSize: "12px",
  color: "rgba(0, 0, 0, 0.8)",
  border: "1px solid transparent",
  borderRadius: "1px",
  backgroundColor: "rgba(0, 0, 0, 0.18)",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    boxShadow: "none",
  },
});
const headers = [
  { label: 'Region', field: 'regionName' },
  { label: 'HQ', field: 'hqName' },
  { label: 'Stockist Id', field: 'stockistCode' },
  { label: 'Stockist Name', field: 'stockistName' },
  { label: 'Sale', field: 'netSale' },
  { label: 'Gross Sale', field: 'Gross_Sales' },
  { label: 'Non-Saleable Return', field: 'Non_SaleableReturn' },
  { label: 'Non-Saleable Return %', field: 'Non_SaleableReturnPer' },
  { label: 'Saleable Return', field: 'SaleableReturn' },
  { label: 'Saleable Return %', field: 'SaleableReturnPer' }
];
const StockistTable: React.FC = React.memo(() => {
  const { kpiData, salesUnit, loading } = useSalesContext() as { kpiData: any; salesUnit: "Lacs" | "Crores"; loading: boolean };
  const kipdata = kpiData?.kpi_data;
 const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
 const data = kipdata?.card4_Stockist?.map((item: any) => ({
    regionName: item.RegionName || "0",
    hqName: item.HqName || "0",
    stockistCode: item.StockistCode || "0",
    stockistName: item.StockistName ,
    netSale: item.Net_Sale ,
    Gross_Sales : item.Gross_Sales ,
    Non_SaleableReturn: item.Non_SaleableReturn ,
    Non_SaleableReturnPer: item.Non_SaleableReturnPer ,
    SaleableReturn: item.SaleableReturn ,
    SaleableReturnPer: item.SaleableReturnPer ,
  })) || [];

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortField(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

const grandTotalRow = data.find(row => row.stockistName === 'Grand Total');
const normalRows = data.filter(row => row.stockistName !== 'Grand Total');

const sortedNormalRows = [...normalRows].sort((a, b) => {
  const aValue = a[sortField];
  const bValue = b[sortField];

  if (aValue === undefined || bValue === undefined) return 0;

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  }

  return sortOrder === 'asc'
    ? String(aValue).localeCompare(String(bValue))
    : String(bValue).localeCompare(String(aValue));
});

const sortedData = grandTotalRow ? [grandTotalRow, ...sortedNormalRows] : sortedNormalRows;

 
const getStickyStyle = (index: number): React.CSSProperties => {
  const baseOffsetsLarge = [0, 90, 179, 287];
      const baseOffsetsMedium = [0, 91, 180, 270]; // Example values for medium screens
  const baseOffsetsSmall = [0, 112, 233, 337];


  const width = window.innerWidth;
  let offsets: number[];

  if (width >= 1000) {
    offsets = baseOffsetsLarge;
  } else if (width >= 700) {
    offsets = baseOffsetsMedium;
  } else {
    offsets = baseOffsetsSmall;
  }

  if (index < offsets.length) {
    return {
      position: 'sticky',
      left: offsets[index],
      zIndex: 100,
      minWidth: '90px',
    };
  }
  return {};
};

const getStickyStyle2 = (index: number): React.CSSProperties => {
   const baseOffsetsLarge = [0, 90, 179, 287];
const baseOffsetsMedium = [0, 91, 180, 270]; // Example values for medium screens
  const baseOffsetsSmall = [0, 112, 233, 337];


  const width = window.innerWidth;
  let offsets: number[];

  if (width >= 1000) {
    offsets = baseOffsetsLarge;
  } else if (width >= 700) {
    offsets = baseOffsetsMedium;
  } else {
    offsets = baseOffsetsSmall;
  }

  if (index < offsets.length) {
    return {
      position: 'sticky',
      left: offsets[index],
      zIndex: 1,
      backgroundColor: '#fff',
          minWidth: '110px',
    };
  }
  return {};
};

const handleDownload = (e) => {
  console.log("Download button clicked", e);

  const headers = [
    'Region', 'HQ', 'Stockist Id', 'Stockist Name', 
    'Sale', 'Gross Sale', 'Non-Saleable Return', 
    'Non-Saleable Return %', 'Saleable Return', 'Saleable Return %'
  ];

  const fields = [
    'regionName', 'hqName', 'stockistCode', 'stockistName', 
    'netSale', 'Gross_Sales', 'Non_SaleableReturn', 
    'Non_SaleableReturnPer', 'SaleableReturn', 'SaleableReturnPer'
  ];

  const salesUnit = "Lacs"; // Replace with dynamic logic if needed

  const csvData = data.map((row) => {
    const formattedRow = {};

    fields.forEach((field, index) => {
      let value = row[field] ?? '';

      if (field !== 'Non_SaleableReturnPer' && field !== 'SaleableReturnPer') {
        if (typeof value === 'number' || !isNaN(Number(value))) {
          value = Number(value);

          if (salesUnit === "Lacs") {
            value = value / 100000;
          } else if (salesUnit === "Crores") {
            value = value / 10000000;
          }

          value = value.toFixed(2);
        }
      }

      if (field.endsWith("Per") && value !== "N/A") {
        value = `${value}%`;
      }

      formattedRow[headers[index]] = value;
    });

    return formattedRow;
  });

  // Add headers as first row
  const worksheet = XLSX.utils.json_to_sheet(csvData, { header: headers });
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const isReactNativeWebView = !!window.ReactNativeWebView;
  const isInIframe = window.self !== window.top;
  // Message object for iframe/ReactNativeWebView

  const message = {
    type: "DOWNLOAD_CSV",
    csv: btoa(unescape(encodeURIComponent(csv))),
    fileName: "stockist_data.csv",
    mimeType: "text/csv"
  };
  // Download as CSV

   if (isReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  } else if (isInIframe) {
    window.parent.postMessage(JSON.stringify(message), "https://bsvdashqa.mankindpharma.in");
  } else {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "Stockist_Sale_Report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  alert("File downloaded successfully!");
  }

};


  
  const formatSaleValue = (number: string | number): string => {
    if (isNaN(Number(number)) || !isFinite(Number(number))) return "";
    const unit = salesUnit || "Lacs";
    // Remove hyphen from the number
    number = Number(number);
  
    if (unit === "Crores") {
      return (number / 10000000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else if (unit === "Lacs") {
      return (number / 100000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      return number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  return (
    <Box className="breakwise-second-container">
       <Box className="breakwise-header">
              <Box className="breakwise-title-container" style={{ display: 'flex', justifyContent: 'space-between', height: '90px' }}>
                <Typography className="breakwise-title">Stockist Wise Sale ({salesUnit || "Lacs"})</Typography>
                <Button variant="outlined" className="breakwise-download-button" onClick={handleDownload}>
                  Download MIS
                </Button>
              </Box>
            </Box>
       <Box className="breakwise-table-container" style={{ display: 'flex' }} sx={{ overflowX: 'auto' }}>
      <TableContainer component={Paper} className="fixed-table-container" >
        <Table stickyHeader size="small">
          <TableHead className="stockist-fixed-table-head" style={{ fontSize: '14px' }}>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell key={header.field} style={{ ...getStickyStyle(index) }} className="stockist-custom-font">
                  <TableSortLabel
                    active={sortField === header.field}
                    direction={sortField === header.field ? sortOrder : 'asc'}
                    onClick={() => handleSort(header.field)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
     <TableBody className="fixed-table-body">
  {sortedData.map((row, index) => (
    <TableRow key={index} style={{ backgroundColor: '#ffffff' }}>
      {headers.map((header, fieldIndex) => {
        const field = header.field;
        const rawValue = row[field];

        const isFormatted = ['netSale', 'Gross_Sales', 'Non_SaleableReturn', 'SaleableReturn'].includes(field);

        let value;

      if (isFormatted) {
  const isGrandTotal = row.stockistName === 'Grand Total';
  let scaledValue = Number(rawValue);

  if (!isNaN(scaledValue) && isFinite(scaledValue)) {
    if (salesUnit === 'Lacs') {
      scaledValue = scaledValue / 100000;
    } else if (salesUnit === 'Crores') {
      scaledValue = scaledValue / 10000000;
    }

    value = scaledValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } else {
    value = rawValue;
  }
}
 else {
          // Treat as percentage: two decimals + '%' sign
          const numericVal = parseFloat(rawValue);
          value = !isNaN(numericVal) ? `${numericVal.toFixed(2)}%` : rawValue;
        }

        return (
<TableCell
  key={fieldIndex}
  className="stockist-custom-font"
  style={{
    ...getStickyStyle2(fieldIndex),
    whiteSpace: ['stockistCode', 'hqName', 'regionName', 'stockistName'].includes(field)
      ? 'pre-wrap'
      : 'nowrap',
    wordBreak: ['stockistCode', 'hqName', 'regionName', 'stockistName'].includes(field)
      ? 'break-word'
      : 'normal',
    minWidth: field === 'hqName' ? '70px' : 'unset', 
  }}
>
  <Tooltip
    title={
      ['stockistCode', 'hqName', 'regionName', 'stockistName'].includes(field)
        ? String(rawValue)
        : String(value)
    }
    arrow
  >
    <span>
      {['stockistCode', 'hqName', 'regionName', 'stockistName'].includes(field)
        ? rawValue
        : value}
    </span>
  </Tooltip>
</TableCell>


        );
      })}
    </TableRow>
  ))}
</TableBody>


        </Table>
      </TableContainer>
    </Box>
    </Box>
  );
});

export default StockistTable;



